import { Tool } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { TOOLS_URL } from '../config/api-config';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useToolsApi = () => {
    const { getAuthToken } = useAuth();
    const getHeaders = createApiHeadersGetter(getAuthToken);
  
    const getTools = async (getAllTools: boolean = false): Promise<Tool[]> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${TOOLS_URL}?getAllTools=${getAllTools}`,
          {
            method: 'GET',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch tools');
        }
        return response.json();
      } catch (error) {
        console.error('Error fetching Tools:', error);
        throw error;
      }
    };

    const getTool = async (id: string): Promise<Tool> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${TOOLS_URL}/${id}`,
          {
            method: 'GET',
            headers
          }
        );
        console.log('[getTool] Response:', response);
        
        if (!response.ok) {
          let errorMessage = 'Failed to fetch Tool';
          let errorDetails;
          
          try {
            // Try to parse error response as JSON
            const errorData: APIError = await response.json();
            errorMessage = errorData.message || errorMessage;
            errorDetails = errorData.details;
          } catch (parseError) {
            // If JSON parsing fails, use status text
            errorMessage = response.statusText || errorMessage;
          }
          
          throw new ApiError(errorMessage, response.status, errorDetails);
        }
  
        return await response.json();
      } catch (error: any) {
        console.error('Error fetching Tool:', error);
        // If it's already an ApiError, rethrow it
        if (error instanceof ApiError) {
          throw error;
        }
        // Otherwise wrap it in an ApiError
        throw new ApiError(error.message || 'Unknown error occurred', 500);
      }
    };

    const createTool = async (tool: Omit<Tool, 'id'>): Promise<Tool> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(TOOLS_URL,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(tool),
          }
        );
  
        if (!response.ok) {
          console.log('[createTool] Error creating tool response:', response);
          try {
            const errorData: APIError = await response.json();
            console.log('[createTool] Error creating tool errorData:', errorData);
            throw new Error(errorData.message || 'Failed to create tool');
          } catch (parseError) {
            console.log('[createTool] Error parsing JSON:', String(parseError));
            throw new Error(String(parseError));
          }
        }
        return response.json();
      } catch (error) {
        console.error('Error creating Tool:', error);
        throw error;
      }
    };
  
    const updateTool = async (tool: Partial<Tool>): Promise<Tool> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${TOOLS_URL}/${tool.toolId}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(tool),
          }
        );
        if (!response.ok) {
          throw new Error('Failed to update tool');
        }
        return response.json();
      } catch (error) {
        console.error('Error updating Tool:', error);
        throw error;
      }
    };
  
    const deleteTool = async (id: string): Promise<void> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${TOOLS_URL}/${id}`,
          {
            method: 'DELETE',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to delete Tool');
        }
        return;
      } catch (error) {
        console.error('Error deleting Tool:', error);
        throw error;
      }
    };

    const testTool = async (id: string, parameters: any): Promise<any> => {
      try {
        console.log(parameters);
        const headers = await getHeaders();
        const response = await fetch(`${TOOLS_URL}/${id}/test`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(parameters),
          }
        );
        
        // Always try to parse the response as JSON, regardless of success or failure
        const data = await response.json().catch(err => {
          console.error('Error parsing response:', err);
          return { error: 'Failed to parse response' };
        });
        
        // Even if the response is not OK, return the exact data from the API
        // This ensures the raw API response body is passed back to the UI
        if (!response.ok) {
          console.error('Test tool failed with status:', response.status);
          console.error('Error response data:', data);
          return data; // Return the raw API response body
        }
        
        // Return the data exactly as received from the API, without transforming it
        return data;
      } catch (error) {
        console.error('Error testing Tool:', error);
        // For network errors or other exceptions, create an error object
        return {
          error: true,
          message: error instanceof Error ? error.message : 'Unknown error occurred'
        };
      }
    };
  
    return {
      getTools,
      getTool,
      createTool,
      updateTool,
      deleteTool,
      testTool
    };
  };